import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Button } from "../ui/Button";
import {
  formatNumber,
  formatDecimal,
  formatPercent,
  formatSignedNumber,
  formatCurrency,
  deltaTextClass,
} from "./format";
import { downloadSectionedXlsx } from "./xlsxExport";

function FunnelBar({ stage, count, rate, maxCount }) {
  const widthPct = maxCount > 0 ? Math.max((count / maxCount) * 100, 4) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{stage}</span>
        <span className="text-muted-foreground">
          {count.toLocaleString()}
          {rate !== null && rate !== undefined ? ` · ${formatPercent(rate)} of prior stage` : ""}
        </span>
      </div>
      <div className="h-3 w-full rounded bg-muted overflow-hidden">
        <div className="h-full rounded bg-primary" style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
}

function DeltaTable({ title, rows, emptyLabel, startLabel, endLabel }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>{endLabel}</TableHead>
                <TableHead>{startLabel}</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.station_id}>
                  <TableCell>{row.site_name ? `${row.site_name} (${row.station_id})` : row.station_id}</TableCell>
                  <TableCell>{row.current_active_users.toLocaleString()}</TableCell>
                  <TableCell>{row.previous_active_users.toLocaleString()}</TableCell>
                  <TableCell className={deltaTextClass(row.delta)}>{formatSignedNumber(row.delta)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const SERIES_LINES = [
  { key: "licensed", name: "Licensed", color: "#2563eb" },
  { key: "activated", name: "Launched", color: "#16a34a" },
  { key: "active_users", name: "Active users", color: "#9333ea" },
];

export default function AnalysisTab({ data, loading, error }) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading Analysis report…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!data) return null;

  const { series = [], funnel = [], top_growth = [], top_declines = [], start_month, end_month } = data;
  const maxFunnelCount = funnel.length ? funnel[0].count : 0;
  const monthCount = series.length;

  const handleDownloadXlsx = () => {
    downloadSectionedXlsx(`analysis-${start_month || "start"}-to-${end_month || "end"}.xlsx`, "Analysis", [
      {
        title: "Monthly series",
        columns: [
          { header: "Month", key: "month" },
          { header: "Licensed", key: "licensed" },
          { header: "Launched", key: "activated" },
          { header: "Active Users", key: "active_users" },
          { header: "Recordings", key: "recordings" },
          { header: "Avg Daily Active Users", key: "avg_dau" },
          { header: "Estimated Revenue", key: "revenue", format: "currency" },
        ],
        rows: series,
      },
      {
        title: "Funnel",
        columns: [
          { header: "Stage", key: "stage" },
          { header: "Count", key: "count" },
          { header: "Rate vs Prior Stage", key: "rate_vs_prior_stage", format: "percent" },
        ],
        rows: funnel,
      },
      {
        title: "Top Growth",
        columns: [
          { header: "Site", key: "site_name" },
          { header: "Station ID", key: "station_id" },
          { header: `${end_month} Active`, key: "current_active_users" },
          { header: `${start_month} Active`, key: "previous_active_users" },
          { header: "Delta", key: "delta" },
        ],
        rows: top_growth,
      },
      {
        title: "Top Declines",
        columns: [
          { header: "Site", key: "site_name" },
          { header: "Station ID", key: "station_id" },
          { header: `${end_month} Active`, key: "current_active_users" },
          { header: `${start_month} Active`, key: "previous_active_users" },
          { header: "Delta", key: "delta" },
        ],
        rows: top_declines,
      },
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Analysis</h2>
          <p className="text-sm text-muted-foreground">
            {start_month} through {end_month}
            {monthCount ? ` · ${monthCount} month${monthCount === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <Button variant="outline" onClick={handleDownloadXlsx}>
          Download XLSX
        </Button>
      </div>

      {monthCount > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly trend</CardTitle>
            <CardDescription>Licensed, launched, and active users for each month in the range</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  {SERIES_LINES.map((line) => (
                    <Line
                      key={line.key}
                      type="monotone"
                      dataKey={line.key}
                      name={line.name}
                      stroke={line.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">By month</CardTitle>
          <CardDescription>Each row is a month-end snapshot for that month</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead>Licensed</TableHead>
                  <TableHead>Launched</TableHead>
                  <TableHead>Active users</TableHead>
                  <TableHead>Recordings</TableHead>
                  <TableHead>Avg DAU</TableHead>
                  <TableHead>Est. revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {series.map((row, index) => {
                  const prev = index > 0 ? series[index - 1] : null;
                  return (
                    <TableRow key={row.month}>
                      <TableCell className="whitespace-nowrap">{row.month}</TableCell>
                      <TableCell>
                        {formatNumber(row.licensed)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.licensed - prev.licensed)}`}>
                            {formatSignedNumber(row.licensed - prev.licensed)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatNumber(row.activated)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.activated - prev.activated)}`}>
                            {formatSignedNumber(row.activated - prev.activated)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatNumber(row.active_users)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.active_users - prev.active_users)}`}>
                            {formatSignedNumber(row.active_users - prev.active_users)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatNumber(row.recordings)}
                        {prev && (
                          <span className={`ml-1 text-xs ${deltaTextClass(row.recordings - prev.recordings)}`}>
                            {formatSignedNumber(row.recordings - prev.recordings)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatDecimal(row.avg_dau)}</TableCell>
                      <TableCell>{formatCurrency(row.revenue)}</TableCell>
                    </TableRow>
                  );
                })}
                {series.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No months in the selected range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adoption funnel</CardTitle>
          <CardDescription>Licensed → Launched → Active users, for {end_month}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {funnel.map((stage) => (
            <FunnelBar
              key={stage.stage}
              stage={stage.stage}
              count={stage.count}
              rate={stage.rate_vs_prior_stage}
              maxCount={maxFunnelCount}
            />
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DeltaTable
          title="Top growth (active users)"
          rows={top_growth}
          emptyLabel="No sites gained active users across this range."
          startLabel={start_month}
          endLabel={end_month}
        />
        <DeltaTable
          title="Top declines (active users)"
          rows={top_declines}
          emptyLabel="No sites lost active users across this range."
          startLabel={start_month}
          endLabel={end_month}
        />
      </div>
    </div>
  );
}
